import React, { useRef, useState } from 'react';
import Header from '../../components/header/header';
import Footer from '../../components/footer/footer';
import PageMeta from '../../components/pageMeta/PageMeta';
import { trackEvent } from '../../utils/analytics';
import './contact.css';

const initialForm = {
  name: '',
  email: '',
  company: '',
  workflow: '',
  tools: '',
  timeline: 'Exploring',
};

const openEmailClient = (url) => {
  window.location.href = url;
};

const ContactPage = ({
  contactEndpoint = process.env.REACT_APP_CONTACT_ENDPOINT,
  openEmail = openEmailClient,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const hasStarted = useRef(false);
  const endpoint = contactEndpoint;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleFocus = () => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    trackEvent('contact_form_started');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('submitting');
    setMessage('');

    if (!endpoint) {
      const subject = encodeURIComponent(`Agent build enquiry from ${formData.name}`);
      const body = encodeURIComponent([
        `Name: ${formData.name}`,
        `Email: ${formData.email}`,
        `Company: ${formData.company || 'Not provided'}`,
        `Timeline: ${formData.timeline}`,
        `Current tools: ${formData.tools || 'Not provided'}`,
        '',
        'Workflow or product idea:',
        formData.workflow,
      ].join('\n'));

      trackEvent('contact_email_fallback_opened');
      setStatus('success');
      setMessage('Your email app should open with the project details prefilled. If it does not, email contact@tryouts.tech directly.');
      openEmail(`mailto:contact@tryouts.tech?subject=${subject}&body=${body}`);
      return;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'tryouts.tech' }),
      });

      if (!response.ok) throw new Error('Submission failed');
      setStatus('success');
      setMessage('Thanks—your project note is with us. We will reply within one business day.');
      setFormData(initialForm);
      trackEvent('contact_form_submitted', { timeline: formData.timeline });
    } catch (error) {
      setStatus('error');
      setMessage('We could not send the form. Your details are still here—please retry or email contact@tryouts.tech.');
      trackEvent('contact_form_error');
    }
  };

  return (
    <div className="contact-page">
      <PageMeta
        title="Plan an AI Agent Build | TryOuts"
        description="Tell TryOuts about the workflow or AI product you want to build. Get a focused scoping conversation with our engineering team."
        path="/contact"
      />
      <Header />
      <main id="main-content" className="contact-main">
        <div className="container contact-grid">
          <section className="contact-intro" aria-labelledby="contact-title">
            <p className="eyebrow">Start a project</p>
            <h1 id="contact-title">Bring us the workflow that keeps breaking.</h1>
            <p>Tell us what your team is trying to accomplish, where the current process gets stuck, and which systems are already involved.</p>

            <div className="contact-expectations">
              <h2>What happens next</h2>
              <ol>
                <li><span>01</span><p><strong>We review the workflow.</strong> We look for the decision points, integrations, and unknowns that matter.</p></li>
                <li><span>02</span><p><strong>We hold a focused scoping call.</strong> No generic pitch deck—just the problem, constraints, and useful next step.</p></li>
                <li><span>03</span><p><strong>You receive a practical direction.</strong> That may be a prototype scope, a technical discovery, or a recommendation not to build yet.</p></li>
              </ol>
            </div>

            <div className="contact-direct">
              <span>Prefer email?</span>
              <a href="mailto:contact@tryouts.tech" onClick={() => trackEvent('direct_email_clicked')}>contact@tryouts.tech ↗</a>
            </div>
          </section>

          <section className="contact-form-card" aria-labelledby="form-title">
            <div className="contact-form-card__header">
              <span>Project brief / 01</span>
              <i aria-hidden="true" />
            </div>
            <div className="contact-form-card__body">
              <h2 id="form-title">Plan an agent build</h2>
              <p>Fields marked with * are required.</p>
              <form onSubmit={handleSubmit} onFocus={handleFocus}>
                <div className="contact-form__row">
                  <div className="form-field">
                    <label htmlFor="name">Your name *</label>
                    <input id="name" name="name" value={formData.name} onChange={handleChange} autoComplete="name" required />
                  </div>
                  <div className="form-field">
                    <label htmlFor="email">Work email *</label>
                    <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} autoComplete="email" required />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="company">Company or product</label>
                  <input id="company" name="company" value={formData.company} onChange={handleChange} autoComplete="organization" />
                </div>

                <div className="form-field">
                  <label htmlFor="workflow">What workflow or product do you want to improve? *</label>
                  <textarea id="workflow" name="workflow" rows="6" value={formData.workflow} onChange={handleChange} placeholder="What happens today, where does it break, and what would a useful outcome look like?" required />
                </div>

                <div className="form-field">
                  <label htmlFor="tools">Current tools or systems</label>
                  <input id="tools" name="tools" value={formData.tools} onChange={handleChange} placeholder="For example: HubSpot, Slack, internal APIs, Postgres" />
                </div>

                <div className="form-field">
                  <label htmlFor="timeline">Current stage</label>
                  <select id="timeline" name="timeline" value={formData.timeline} onChange={handleChange}>
                    <option>Exploring</option>
                    <option>Ready to prototype</option>
                    <option>Prototype exists</option>
                    <option>Need production help</option>
                  </select>
                </div>

                {message && (
                  <div className={`status-message status-message--${status === 'error' ? 'error' : 'success'}`} role="status">
                    {message}
                  </div>
                )}

                <button className="button button--primary contact-submit" type="submit" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Sending…' : 'Send project brief'} <span aria-hidden="true">↗</span>
                </button>
                <p className="contact-form__privacy">We only use these details to respond to your enquiry. Sensitive project data can wait until an appropriate agreement is in place.</p>
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
